<script lang="ts">
  type Feature = {
    name: string;
    us: boolean | string;
    monarch: boolean | string;
    copilot: boolean | string;
    ynab: boolean | string;
  };

  const features: Feature[] = [
    {
      name: 'Annual Cost',
      us: '$0 (Free Forever)',
      monarch: '$180/year',
      copilot: '$180/year',
      ynab: '$180/year',
    },
    {
      name: 'Monthly Cost',
      us: 'Free',
      monarch: '$14.99',
      copilot: '$14.99',
      ynab: '$14.99',
    },
    {
      name: 'Privacy-First',
      us: true,
      monarch: false,
      copilot: false,
      ynab: false,
    },
    {
      name: 'Local-Only Storage',
      us: true,
      monarch: false,
      copilot: false,
      ynab: false,
    },
    {
      name: 'AI Categorization',
      us: true,
      monarch: true,
      copilot: true,
      ynab: false,
    },
    {
      name: 'Multi-Bank CSV Import',
      us: true,
      monarch: true,
      copilot: true,
      ynab: true,
    },
    {
      name: 'Budget Tracking',
      us: true,
      monarch: true,
      copilot: true,
      ynab: true,
    },
    {
      name: 'Family/Multi-Person',
      us: true,
      monarch: true,
      copilot: false,
      ynab: true,
    },
    {
      name: 'No Subscription',
      us: true,
      monarch: false,
      copilot: false,
      ynab: false,
    },
    {
      name: 'Works Offline',
      us: true,
      monarch: false,
      copilot: false,
      ynab: false,
    },
  ];

  function renderValue(value: boolean | string): { text: string; class: string } {
    if (typeof value === 'string') {
      return { text: value, class: 'value-text' };
    }
    return value
      ? { text: '✓', class: 'value-yes' }
      : { text: '✗', class: 'value-no' };
  }
</script>

<section class="section comparison-section">
  <div class="container">
    <div class="section-header">
      <h2>Why Pay for What Should Be Free?</h2>
      <p>Compare Keep to the apps charging $180/year for the same features</p>
    </div>

    <div class="comparison-wrapper">
      <div class="comparison-table">
        <div class="table-header">
          <div class="header-cell header-feature">Feature</div>
          <div class="header-cell header-highlight">
            <div class="logo-wrapper">
              <img src="/plutus-icon.png" alt="Keep" class="product-logo" />
            </div>
            <div class="product-name">Keep</div>
            <div class="product-badge">Free Forever</div>
          </div>
          <div class="header-cell">
            <div class="product-name">Monarch Money</div>
          </div>
          <div class="header-cell">
            <div class="product-name">Copilot</div>
          </div>
          <div class="header-cell">
            <div class="product-name">YNAB</div>
          </div>
        </div>

        {#each features as feature}
          <div class="table-row">
            <div class="feature-cell">{feature.name}</div>
            <div class="value-cell cell-highlight">
              <span class={renderValue(feature.us).class}>
                {renderValue(feature.us).text}
              </span>
            </div>
            <div class="value-cell">
              <span class={renderValue(feature.monarch).class}>
                {renderValue(feature.monarch).text}
              </span>
            </div>
            <div class="value-cell">
              <span class={renderValue(feature.copilot).class}>
                {renderValue(feature.copilot).text}
              </span>
            </div>
            <div class="value-cell">
              <span class={renderValue(feature.ynab).class}>
                {renderValue(feature.ynab).text}
              </span>
            </div>
          </div>
        {/each}
      </div>
    </div>

    <p class="comparison-note">
      * Pricing and features as of February 2026. Competitor features may vary based on plan.
    </p>
  </div>
</section>

<style>
  .comparison-section {
    background: #ffffff;
  }

  .section-header {
    text-align: center;
    max-width: 700px;
    margin: 0 auto var(--spacing-2xl);
  }

  .section-header h2 {
    font-size: var(--font-size-3xl);
    margin-bottom: var(--spacing-sm);
  }

  .section-header p {
    font-size: var(--font-size-lg);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .comparison-wrapper {
    overflow-x: auto;
    margin-bottom: var(--spacing-md);
  }

  .comparison-table {
    min-width: 800px;
    background: var(--color-background);
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-md);
  }

  .table-header {
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr;
    gap: 1px;
    background: var(--color-border);
  }

  .header-cell {
    background: var(--color-background);
    padding: var(--spacing-lg) var(--spacing-md);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xs);
  }

  .header-feature {
    text-align: left;
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-base);
    color: var(--color-text-primary);
  }

  .header-highlight {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    border: 2px solid var(--color-primary);
  }

  .logo-wrapper {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-sm);
  }

  .product-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  .product-name {
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
  }

  .product-badge {
    font-size: 10px;
    color: var(--color-primary);
    font-weight: var(--font-weight-semibold);
    background: white;
    padding: 2px var(--spacing-xs);
    border-radius: var(--radius-full);
  }

  .table-row {
    display: grid;
    grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr;
    gap: 1px;
    background: var(--color-border);
  }

  .table-row:nth-child(even) .feature-cell,
  .table-row:nth-child(even) .value-cell {
    background: var(--color-background-secondary);
  }

  .feature-cell {
    background: var(--color-background);
    padding: var(--spacing-md);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
  }

  .value-cell {
    background: var(--color-background);
    padding: var(--spacing-md);
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .cell-highlight {
    background: var(--color-primary-light) !important;
  }

  .value-yes {
    color: var(--color-primary);
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
  }

  .value-no {
    color: var(--color-text-muted);
    font-size: var(--font-size-lg);
    opacity: 0.4;
  }

  .value-text {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    font-size: var(--font-size-xs);
  }

  .comparison-note {
    text-align: center;
    font-size: 10px;
    color: var(--color-text-muted);
    font-style: italic;
    margin: var(--spacing-md) 0 0;
  }

  @media (max-width: 968px) {
    .section-header h2 {
      font-size: var(--font-size-2xl);
    }

    .comparison-wrapper {
      margin: 0 calc(var(--spacing-lg) * -1) var(--spacing-md);
      padding: 0 var(--spacing-lg);
    }

    .comparison-table {
      min-width: 700px;
    }
  }
</style>
